const CLIENT_PALETTE = [
  "#5FCAB6",
  "#E7C53E",
  "#F05668",
  "#3A74D0",
  "#81C363",
  "#E8943A",
  "#B462D1",
];

const OWNER_PALETTE = [
  "var(--vp-turquoise-100)",
  "var(--vp-geel-100)",
  "var(--vp-rood-100)",
  "var(--vp-blauw-120)",
  "var(--vp-paars-100)",
  "var(--vp-groen-100)",
  "var(--vp-oranje-100)",
];

function hashString(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function colorForClient(name) {
  if (!name) return "var(--vp-grijs-40)";
  return CLIENT_PALETTE[hashString(name) % CLIENT_PALETTE.length];
}

export function colorForOwner(name) {
  if (!name) return "var(--vp-grijs-40)";
  return OWNER_PALETTE[hashString(name) % OWNER_PALETTE.length];
}

export function initialsFromName(name) {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function deriveMethod(doc) {
  if (doc.type) return doc.type;
  if (doc.method_description) {
    const first = String(doc.method_description)
      .split(/[.!?\n]/)[0]
      .trim();
    if (first.length > 0 && first.length <= 40) return first;
  }
  return "";
}

function deriveOwner(doc) {
  const r = Array.isArray(doc.researchers) ? doc.researchers[0] : null;
  if (!r || !r.name) return null;
  return {
    name: r.name,
    initials: initialsFromName(r.name),
    color: colorForOwner(r.name),
    photo: r.photo || r.image || null,
    email: r.email || null,
    uid: r.uid || null,
  };
}

const VALID_STATUSES = new Set(["draft", "in_progress", "done"]);
// Map legacy status keys written by older versions of the app onto the
// current set so existing Firestore docs render with the new labels.
const STATUS_ALIASES = { analysis: "in_progress", live: "done" };

function deriveStatus(doc) {
  let s = doc && typeof doc.status === "string" ? doc.status : null;
  if (s && STATUS_ALIASES[s]) s = STATUS_ALIASES[s];
  return s && VALID_STATUSES.has(s) ? s : "draft";
}

function deriveCreated(doc) {
  // Prefer the explicit createdAt field written on project creation (unix s).
  if (typeof doc.createdAt === "number") return new Date(doc.createdAt * 1000);
  // Fall back to Firestore server-side createTime captured during fetchProjects.
  if (doc._firestoreCreateTime) {
    const t = doc._firestoreCreateTime;
    if (typeof t.toDate === "function") return t.toDate();
    if (t.seconds != null) return new Date(t.seconds * 1000);
    if (t instanceof Date) return t;
  }
  // Last-resort fallback: earliest file timestamp.
  if (Array.isArray(doc.files) && doc.files.length > 0) {
    const earliest = doc.files
      .map((f) => f.createdAt)
      .filter((v) => typeof v === "number")
      .sort((a, b) => a - b)[0];
    if (earliest) return new Date(earliest * 1000);
  }
  return null;
}

export function normalizeProject(doc) {
  const client = doc.client_name || "Unassigned";
  return {
    uid: doc.uid,
    title: doc.title || "(untitled)",
    client,
    clientColor: doc.client_name
      ? colorForClient(client)
      : "var(--vp-grijs-40)",
    method: deriveMethod(doc),
    n: typeof doc.n === "number" ? doc.n : null,
    owner: deriveOwner(doc),
    status: deriveStatus(doc),
    created: deriveCreated(doc),
  };
}

export function fmtDate(d) {
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function relTime(d, now = new Date()) {
  if (!d) return "";
  const days = Math.round((now - d) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}
